import { createColumnHelper } from '@tanstack/react-table';

import { FilterType, ReportOrderType, ReportTradeSide, Trade, TradeStatus } from '../_openapi/portfolio-optimizer';
import { getAvailableAssetClasses, getAvailableAssetTypes } from '../utils/assetUtils';
import { AssetClassTag } from '../widgets/AssetClassTag';
import { baseCol, dateCol, moneyCol, numberCol, textCol } from '../widgets/Table/columnHelpers/colDef';
import { FlagCell } from '../widgets/Table/FlagCell';
import { TradeSideTag } from '../widgets/TradeSideTag';

const columnHelper = createColumnHelper<Trade>();
export const tradeHistoryListColumns = [
    columnHelper.accessor('pId', textCol({ id: 'pId' })),
    columnHelper.accessor('tradeDate', dateCol({ id: 'tradeDate' })),
    columnHelper.accessor('portfolioGuid', textCol({ id: 'portfolioGuid' })),
    columnHelper.accessor('portfolioName', textCol({ id: 'portfolioName' })),
    columnHelper.accessor('householdName', textCol({ id: 'householdName' })),
    columnHelper.accessor('aC', textCol({ id: 'aC' })),
    columnHelper.accessor('ndvrTradeId', textCol({ id: 'ndvrTradeId' })),
    columnHelper.accessor('sId', textCol({ id: 'sId' })),
    columnHelper.accessor('description', textCol({ id: 'description' })),
    columnHelper.accessor('assetClass', {
        ...baseCol({ id: 'assetClass' }),
        cell: ({ getValue }) => <AssetClassTag assetClass={getValue()} />,
        meta: {
            filterOptionsFn: getAvailableAssetClasses,
            filterType: FilterType.LIST,
        },
    }),
    columnHelper.accessor(
        'assetType',
        textCol({
            id: 'assetType',
            meta: {
                filterOptionsFn: getAvailableAssetTypes,
                filterType: FilterType.LIST,
            },
        }),
    ),
    columnHelper.accessor(
        'subAssetType',
        textCol({
            id: 'subAssetType',
            meta: {
                filterOptionsFn: getAvailableAssetTypes,
                filterType: FilterType.LIST,
            },
        }),
    ),
    columnHelper.accessor('quantity', numberCol({ id: 'quantity' })),
    columnHelper.accessor('tradePrice', moneyCol({ id: 'tradePrice' })),
    columnHelper.accessor('tradeValue', moneyCol({ id: 'tradeValue' })),
    columnHelper.accessor('netExposure', moneyCol({ id: 'netExposure' })),
    columnHelper.accessor('grossExposure', moneyCol({ id: 'grossExposure' })),
    columnHelper.accessor('side', {
        ...baseCol({ id: 'side' }),
        cell: ({ getValue }) => <TradeSideTag side={getValue()} />,
        meta: {
            filterOptionsFn: () => Object.values(ReportTradeSide),
            filterType: FilterType.LIST,
        },
    }),
    columnHelper.accessor(
        'tradeStatus',
        textCol({
            id: 'tradeStatus',
            meta: {
                filterOptionsFn: () => Object.values(TradeStatus),
                filterType: FilterType.LIST,
            },
        }),
    ),
    columnHelper.accessor('commission', moneyCol({ id: 'commission' })),
    columnHelper.accessor(
        'orderType',
        textCol({
            id: 'orderType',
            meta: {
                filterOptionsFn: () => Object.values(ReportOrderType),
                filterType: FilterType.LIST,
            },
        }),
    ),

    columnHelper.accessor('referencePortfolio', textCol({ id: 'referencePortfolio' })),
    columnHelper.accessor('allocationType', textCol({ id: 'allocationType' })),
    columnHelper.accessor('investmentAlgo', textCol({ id: 'investmentAlgo' })),
    columnHelper.accessor('investmentSleeve', textCol({ id: 'investmentSleeve' })),
    columnHelper.accessor('nav', moneyCol({ id: 'nav' })),
    columnHelper.accessor('scId', textCol({ id: 'scId' })),
    columnHelper.accessor('coId', textCol({ id: 'coId' })),
    columnHelper.accessor('priceMultiplier', numberCol({ id: 'priceMultiplier' })),
    columnHelper.accessor('contractMultiplier', numberCol({ id: 'contractMultiplier' })),
    columnHelper.accessor('symbol', textCol({ id: 'symbol' })),
    columnHelper.accessor('primaryExchange', textCol({ id: 'primaryExchange' })),
    columnHelper.accessor('cusip', textCol({ id: 'cusip' })),
    columnHelper.accessor('isin', textCol({ id: 'isin' })),
    columnHelper.accessor('sedol', textCol({ id: 'sedol' })),
    columnHelper.accessor('country', {
        ...baseCol({ id: 'country' }),
        cell: ({ getValue }) => <FlagCell alpha3={getValue()} />,
    }),
    columnHelper.accessor('mic', textCol({ id: 'mic' })),
    columnHelper.accessor('gicsCode', textCol({ id: 'gicsCode' })),
    columnHelper.accessor('gicsSector', textCol({ id: 'gicsSector' })),
    columnHelper.accessor('gicsIndustry', textCol({ id: 'gicsIndustry' })),
    columnHelper.accessor('issuer', textCol({ id: 'issuer' })),
    columnHelper.accessor('coupon', numberCol({ id: 'coupon' })),
    columnHelper.accessor('maturityDate', dateCol({ id: 'maturityDate' })),
    columnHelper.accessor('issueDate', dateCol({ id: 'issueDate' })),
    columnHelper.accessor('expirationDate', dateCol({ id: 'expirationDate' })),
    columnHelper.accessor('firstNoticeDate', dateCol({ id: 'firstNoticeDate' })),
    columnHelper.accessor('settlementMethod', textCol({ id: 'settlementMethod' })),
    columnHelper.accessor('minTick', numberCol({ id: 'minTick' })),
    columnHelper.accessor('putCall', textCol({ id: 'putCall' })),
    columnHelper.accessor('underlyingSymbol', textCol({ id: 'underlyingSymbol' })),
    columnHelper.accessor('brokerId', textCol({ id: 'brokerId' })),
];
